import React, { useContext, useEffect, useState } from 'react'
import { MappedFilters } from './MappedFilters'
import { useTranslation } from 'react-i18next';
import { TagFilterContext } from '../../../../context/TagFilterContext';

const PORTFOLIO_NAME_SESSION_KEY = 'ONE_PAGER_HEADER_PORTFOLIO';
const FILTERS_APPLIED_SESSION_KEY = 'ONE_PAGER_HEADER_FILTERS';
const GENERAL_DATA_SESSION_KEY = 'ONE_PAGER_HEADER_GENERAL_DATA';
const canUseSessionStorage = () => typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';

const readSessionValue = (key: string) => {
    if (!canUseSessionStorage()) return null;
    return sessionStorage.getItem(key);
};

const readSessionJson = (key: string) => {
    if (!canUseSessionStorage()) return null;
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch (error) {
        sessionStorage.removeItem(key);
        return null;
    }
};

const isStructuredFilters = (filters: any) =>
    Array.isArray(filters) && filters.length >= 3 && (filters[1] || filters[2]);

const HeaderReport = ({ portfolioName, filtersApplied, generalData }) => {
    const { t } = useTranslation();
    const { flattenedOptions } = useContext<any>(TagFilterContext);
    const [storedPortfolioName, setStoredPortfolioName] = useState(() => portfolioName || readSessionValue(PORTFOLIO_NAME_SESSION_KEY) || '');
    const [storedFiltersApplied, setStoredFiltersApplied] = useState(() => {
        const stored = readSessionJson(FILTERS_APPLIED_SESSION_KEY);
        if (isStructuredFilters(stored)) return stored;
        if (isStructuredFilters(filtersApplied)) return filtersApplied;
        return [];
    });
    const [storedGeneralData, setStoredGeneralData] = useState(() => generalData || readSessionJson(GENERAL_DATA_SESSION_KEY) || {});

    useEffect(() => {
        if (!portfolioName || !canUseSessionStorage()) return;
        setStoredPortfolioName(portfolioName);
        sessionStorage.setItem(PORTFOLIO_NAME_SESSION_KEY, portfolioName);
    }, [portfolioName]);

    useEffect(() => {
        if (!canUseSessionStorage() || !isStructuredFilters(filtersApplied)) return;
        setStoredFiltersApplied(filtersApplied);
        try {
            sessionStorage.setItem(FILTERS_APPLIED_SESSION_KEY, JSON.stringify(filtersApplied));
        } catch (error) {
            sessionStorage.removeItem(FILTERS_APPLIED_SESSION_KEY);
        }
    }, [filtersApplied]);

    useEffect(() => {
        if (!generalData || !canUseSessionStorage()) return;
        setStoredGeneralData(generalData);
        try {
            sessionStorage.setItem(GENERAL_DATA_SESSION_KEY, JSON.stringify(generalData));
        } catch (error) {
            sessionStorage.removeItem(GENERAL_DATA_SESSION_KEY);
        }
    }, [generalData]);

    const effectivePortfolioName = storedPortfolioName || portfolioName || '';
    const effectiveFilters = storedFiltersApplied || filtersApplied || [];
    const effectiveGeneralData = storedGeneralData || generalData || {};

    const getByCategory = (category: string) => {
        if (flattenedOptions && flattenedOptions.length) {
            const matches = flattenedOptions.filter(o => o.category === category).map(o => o.label);
            return matches.length ? matches.join(', ') : null;
        }
        return null;
    }

    const sectorsText = (effectiveFilters && effectiveFilters[1]) || getByCategory('sector') || null;
    const divisionText = (effectiveFilters && effectiveFilters[2]) || getByCategory('division') || null;

    return (
        <header className="c-title">
            <div className="summary-header">
                <h2>
                    {t('one_pager_report.wallet')} {effectivePortfolioName}
                </h2>
                <p className="region_title">
                    {effectiveGeneralData.regions && (
                        <>
                            {effectiveGeneralData.regions === "CAN" ? (
                                <>
                                    <strong>{t('one_pager_report.region')}:</strong> {`${t('one_pager_report.departament-andean-group')} (${effectiveGeneralData.regions})`}
                                </>
                            ) : (
                                <>
                                    <strong>{t('one_pager_report.region')}:</strong> {effectiveGeneralData.regions}
                                </>
                            )}
                        </>
                    )}
                    {effectiveGeneralData.countries && (
                        <>
                            {(effectiveGeneralData.regions || sectorsText || divisionText) && (", ")}<strong>{t('one_pager_report.country')}:</strong>{` ${effectiveGeneralData.countries}`}
                        </>
                    )}
                    {sectorsText && (
                        <>
                            {(effectiveGeneralData.regions || effectiveGeneralData.countries || divisionText) && (", ")}<strong>{t('one_pager_report.sectors')}:</strong>{` ${sectorsText}`}
                        </>
                    )}
                    {divisionText && (
                        <>
                            {(effectiveGeneralData.regions || effectiveGeneralData.countries || sectorsText) && (", ")}<strong>{t('one_pager_report.division')}:</strong>{` ${divisionText}`}
                        </>
                    )}
                </p>
            </div>
            <div className="summary-header text-align-right">
                <p className='right-title-report'>
                    {t('one_pager_report.one_pager_report')}{' '}
                    <span className='suptitled_report'>{t('app_name').toUpperCase()}</span>
                </p>
                <p className='creation-date'>
                    {t('one_pager_report.creation')}: {effectiveGeneralData?.creationDate}
                </p>
            </div>
        </header>
    )
}

export default HeaderReport
